import './input.css';

import * as React from 'react';

import { cn } from '@/services/utils';

import { Icon } from '../Icon';

export interface InputProps
    extends React.InputHTMLAttributes<HTMLInputElement> {
        borderless?: boolean;
        gridMode?: boolean;
        icon?: React.ComponentType<React.ComponentPropsWithoutRef<'svg'>> | string;
        suffixIcon?: React.ComponentType<React.ComponentPropsWithoutRef<'svg'>> | string;
        itemEditor?: boolean;
    }

const Input = React.forwardRef<HTMLInputElement, InputProps>(
    ({ borderless, gridMode = false, className, type, icon, suffixIcon, itemEditor = false, onBlur, onKeyDown, ...props }, ref) => {
        const isTabbingOutRef = React.useRef(false);

        const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Tab') {
                isTabbingOutRef.current = true;
            }
            onKeyDown?.(e);
        };

        const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
            if (isTabbingOutRef.current) {
                isTabbingOutRef.current = false;
                window.setTimeout(() => onBlur?.(e), 0);
                return;
            }
            onBlur?.(e);
        };

        const isDisabled = props.disabled;

        // Handle cases with icons
        if (icon || suffixIcon) {
            if (!itemEditor) {
                return (
                    <div className={cn('tw-flex tw-flex-row', className)}>
                        {icon && (
                            <div className="tw-w-10 tw-h-9 tw-bg-gray-200 tw-flex tw-items-center tw-justify-center disabled:tw-bg-fill-secondary">
                                <Icon
                                    icon={icon}
                                    size='sm'
                                    disabled={isDisabled}/>
                            </div>
                        )}
                        <div className="tw-relative tw-flex-1">
                            <input
                                type={type}
                                className={cn(
                                    `input
                    ${borderless ? 'borderless-input' : 'bordered-input'}
                    tw-flex
                    tw-h-9
                    tw-w-full
                    tw-py-2
                    lg-font-dynamic
                    tw-ring-offset-background
                    disabled:tw-text-t-inactive
                    ${icon ? 'tw-pl-3' : 'tw-pl-3'}
                    ${suffixIcon ? 'tw-pr-10' : 'tw-pr-3'}`,
                                    !gridMode && `
                                disabled:tw-cursor-not-allowed
                                disabled:tw-border
                                disabled:tw-border-solid
                                disabled:tw-border-primary
                                disabled:tw-bg-fill-secondary
                                `,
                                    gridMode && 'grid-row-highlightable-input',
                                    className
                                )}
                                ref={ref}
                                onKeyDown={handleKeyDown}
                                onBlur={handleBlur}
                                {...props}
                            />
                            {suffixIcon && (
                                <div className="tw-absolute tw-right-3 tw-top-1/2 tw-transform tw--translate-y-1/2 tw-pointer-events-none disabled:tw-bg-fill-secondary">
                                    <Icon
                                        icon={suffixIcon}
                                        size='sm'
                                        disabled={isDisabled}/>
                                </div>
                            )}
                        </div>
                    </div>
                );
            }
        }

        // Handle case with only suffix icon (no prefix icon)
        if (suffixIcon && !icon) {
            return (
                <div className={cn('tw-relative', className)}>
                    <input
                        type={type}
                        className={cn(
                            `input
                            ${borderless ? 'borderless-input tw-px-2 tw-ml-1 tw-mr-1' : 'bordered-input tw-px-3'}
                            tw-flex
                            tw-h-9
                            tw-w-full
                            lg-font-dynamic
                            tw-text-t-primary
                            tw-py-2
                            tw-ring-offset-background
                            tw-pointer-events-auto
                            !tw-user-select-auto
                            focus-visible:!tw-border-primary-focus
                            disabled:tw-text-t-inactive
                            ${suffixIcon ? 'tw-pr-10' : ''}`,
                            !gridMode && `
                                disabled:tw-cursor-not-allowed
                                disabled:tw-border
                                disabled:tw-border-solid
                                disabled:tw-border-primary
                                disabled:tw-bg-fill-secondary
                                `,
                            gridMode && 'grid-row-highlightable-input',
                        )}
                        ref={ref}
                        onKeyDown={handleKeyDown}
                        onBlur={handleBlur}
                        {...props}
                    />
                    <div className="tw-absolute tw-right-3 tw-top-1/2 tw-transform tw--translate-y-1/2 tw-pointer-events-none">
                        <Icon
                            icon={suffixIcon}
                            size='sm'
                            disabled={isDisabled}/>
                    </div>
                </div>
            );
        }

        // Handle case without icons
        return (
            <input
                type={type}
                className={cn(
                    `input
                    ${borderless || gridMode ? 'borderless-input tw-px-2 tw-ml-1 tw-mr-1' : 'bordered-input tw-px-3'}
                    tw-flex
                    tw-h-9
                    tw-w-full
                    tw-py-2
                    lg-font-dynamic
                    tw-text-t-primary
                    tw-ring-offset-background
                    !tw-pointer-events-auto
                    tw-user-select-auto
                    focus-visible:!tw-border-primary-focus
                    disabled:tw-text-t-inactive`,
                    !gridMode && `
                                disabled:tw-cursor-not-allowed
                                disabled:tw-border
                                disabled:tw-border-solid
                                disabled:tw-border-primary
                                disabled:tw-bg-fill-secondary
                                `,
                    gridMode && 'grid-row-highlightable-input',
                    className,
                )}
                ref={ref}
                onKeyDown={handleKeyDown}
                onBlur={handleBlur}
                {...props}
            />
        );
    },
);

Input.displayName = 'Input';

export { Input };
